import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { EM_DASH } from "@/lib/utils";

import {
  WIRELESS_DEVICE_REPORT_KEY,
  fetchWirelessDeviceReport,
  type WirelessDeviceReportResponse,
} from "./wirelessDeviceReportApi";

function formatTimestamp(value: string | null | undefined): string {
  if (value == null || value.trim() === "") {
    return EM_DASH;
  }
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return value;
  }
  return new Date(parsed).toLocaleString();
}

function IdentitySummary({ report }: { report: WirelessDeviceReportResponse }): JSX.Element {
  const { identity } = report;
  return (
    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">IMEI</dt>
        <dd className="font-mono text-sm">{identity.imei ?? EM_DASH}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ICCID</dt>
        <dd className="font-mono text-sm">{identity.iccid ?? EM_DASH}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Device serial</dt>
        <dd className="font-mono text-sm">
          {identity.deviceLabelSerial != null ? (
            <Link
              className="text-primary hover:underline"
              to={`/manufacturing/device-lookup?serial=${encodeURIComponent(identity.deviceLabelSerial)}`}
            >
              {identity.deviceLabelSerial}
            </Link>
          ) : (
            EM_DASH
          )}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Inferred carrier</dt>
        <dd className="text-sm">{identity.inferredCarrier ?? EM_DASH}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Hub SIM inventory</dt>
        <dd className="text-sm">
          {identity.hubSim != null
            ? `${identity.hubSim.carrier} (${identity.hubSim.isActive ? "active" : "inactive"})`
            : EM_DASH}
        </dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Hub serial_fci</dt>
        <dd className="text-sm">
          {identity.hubSerialFci != null
            ? `#${identity.hubSerialFci.id}${identity.hubSerialFci.archivedAt != null ? " (archived)" : ""}`
            : EM_DASH}
        </dd>
      </div>
    </dl>
  );
}

export function WirelessDeviceReportPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const [draftQuery, setDraftQuery] = useState(initialQuery);
  const submittedQuery = initialQuery.trim();

  const reportQuery = useQuery({
    queryKey: [WIRELESS_DEVICE_REPORT_KEY, submittedQuery],
    queryFn: () => fetchWirelessDeviceReport(submittedQuery),
    enabled: submittedQuery !== "",
    retry: false,
  });

  const report = reportQuery.data;
  const prodSourceLabel = useMemo(() => {
    if (report?.prodWireless.source === "live_rds") {
      return "Live Wireless_Prod RDS";
    }
    if (report?.prodWireless.source === "portal_api_only") {
      return "Wireless portal API only";
    }
    return null;
  }, [report?.prodWireless.source]);

  function submitSearch(): void {
    const next = draftQuery.trim();
    if (next === "") {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: next });
  }

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp
          hoverHint={[
            "Look up a wireless device by IMEI, ICCID, or hub device label serial.",
            "Production check-ins come from the live Wireless_Prod database (not the Supabase mirror).",
            "Carrier sections query Verizon, Vodafone, or AT&T when configured.",
          ].join(" ")}
        >
          Engineering · Wireless device report
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="wireless-device-report-query">IMEI, ICCID, or device serial</Label>
              <Input
                id="wireless-device-report-query"
                value={draftQuery}
                onChange={(event) => setDraftQuery(event.target.value)}
                placeholder="16169007570430, 89148000007439720679, or A01JZ"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submitSearch();
                  }
                }}
              />
            </div>
            <Button type="button" onClick={submitSearch} disabled={reportQuery.isFetching}>
              {reportQuery.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Search
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Prefix with <span className="font-mono">imei:</span>, <span className="font-mono">iccid:</span>, or{" "}
            <span className="font-mono">serial:</span> to force a field.
          </p>
        </CardBody>
      </Card>

      {submittedQuery === "" ? (
        <p className="text-sm text-muted-foreground">Enter an identifier and search to load the report.</p>
      ) : null}

      {reportQuery.isError ? (
        <Card>
          <CardBody>
            <p className="text-sm text-destructive">
              {(reportQuery.error as Error | undefined)?.message ?? "Wireless device report failed."}
            </p>
          </CardBody>
        </Card>
      ) : null}

      {report != null ? (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Resolved identity</CardTitle>
            </CardHeader>
            <CardBody>
              <IdentitySummary report={report} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle>Production wireless</CardTitle>
              {prodSourceLabel != null ? <Badge tone="info">{prodSourceLabel}</Badge> : null}
            </CardHeader>
            <CardBody className="space-y-4">
              {report.prodWireless.message != null ? (
                <p className="text-sm text-muted-foreground">{report.prodWireless.message}</p>
              ) : null}
              {report.prodWireless.portalDevice != null ? (
                <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Portal serial</dt>
                    <dd className="font-mono text-sm">{report.prodWireless.portalDevice.fciSerial ?? EM_DASH}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Portal name</dt>
                    <dd className="text-sm">{report.prodWireless.portalDevice.realName ?? EM_DASH}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Portal status</dt>
                    <dd className="text-sm">{report.prodWireless.portalDevice.status ?? EM_DASH}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Shipment date</dt>
                    <dd className="text-sm">{report.prodWireless.portalDevice.shipmentDate ?? EM_DASH}</dd>
                  </div>
                </dl>
              ) : null}
              {report.prodWireless.checkinSummary != null ? (
                <dl className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Check-ins</dt>
                    <dd className="text-sm">{report.prodWireless.checkinSummary.checkinCount}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">First</dt>
                    <dd className="text-sm">{formatTimestamp(report.prodWireless.checkinSummary.firstCheckin)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Last</dt>
                    <dd className="text-sm">{formatTimestamp(report.prodWireless.checkinSummary.lastCheckin)}</dd>
                  </div>
                </dl>
              ) : null}
              {report.prodWireless.recentCheckins.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Checked in</th>
                        <th className="py-2 pr-4 font-medium">IMEI</th>
                        <th className="py-2 pr-4 font-medium">GPS</th>
                        <th className="py-2 font-medium">Model</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.prodWireless.recentCheckins.map((row) => (
                        <tr key={row.recordId} className="border-b border-border/60">
                          <td className="py-2 pr-4 whitespace-nowrap">{formatTimestamp(row.checkedInAt)}</td>
                          <td className="py-2 pr-4 font-mono">{row.imei}</td>
                          <td className="py-2 pr-4 font-mono">
                            {row.gpsLat != null && row.gpsLong != null
                              ? `${row.gpsLat.toFixed(5)}, ${row.gpsLong.toFixed(5)}`
                              : EM_DASH}
                          </td>
                          <td className="py-2">{row.model ?? EM_DASH}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle>Dev wireless</CardTitle>
              <Badge tone="warning">To do</Badge>
            </CardHeader>
            <CardBody>
              <p className="text-sm text-muted-foreground">{report.devWireless.message}</p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle>Carrier</CardTitle>
              {report.carrier.inferredCarrier != null ? (
                <Badge tone="neutral">{report.carrier.inferredCarrier}</Badge>
              ) : null}
            </CardHeader>
            <CardBody className="space-y-4">
              {report.carrier.message != null ? (
                <p className="text-sm text-muted-foreground">{report.carrier.message}</p>
              ) : null}

              {report.carrier.verizon.queried ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">Verizon</h3>
                  {report.carrier.verizon.error != null ? (
                    <p className="text-sm text-destructive">{report.carrier.verizon.error}</p>
                  ) : null}
                  {report.carrier.verizon.device != null ? (
                    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">State</dt>
                        <dd className="text-sm">{report.carrier.verizon.device.carrierState ?? EM_DASH}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Connected</dt>
                        <dd className="text-sm">{report.carrier.verizon.device.connected === true ? "Yes" : "No"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Last connection</dt>
                        <dd className="text-sm">{formatTimestamp(report.carrier.verizon.device.lastConnectionDate)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">IMEI</dt>
                        <dd className="font-mono text-sm">{report.carrier.verizon.device.deviceIds.imei ?? EM_DASH}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ICCID</dt>
                        <dd className="font-mono text-sm">{report.carrier.verizon.device.deviceIds.iccid ?? EM_DASH}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">MDN</dt>
                        <dd className="font-mono text-sm">{report.carrier.verizon.device.deviceIds.mdn ?? EM_DASH}</dd>
                      </div>
                    </dl>
                  ) : null}
                  {report.carrier.verizon.connectionEvents.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-2 pr-4 font-medium">Occurred</th>
                            <th className="py-2 pr-4 font-medium">Event</th>
                            <th className="py-2 pr-4 font-medium">Bytes</th>
                            <th className="py-2 font-medium">IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.carrier.verizon.connectionEvents.map((row, index) => (
                            <tr key={`${row.occurredAt}-${index}`} className="border-b border-border/60">
                              <td className="py-2 pr-4 whitespace-nowrap">{formatTimestamp(row.occurredAt)}</td>
                              <td className="py-2 pr-4">{row.event ?? EM_DASH}</td>
                              <td className="py-2 pr-4 font-mono">{row.bytesUsed ?? EM_DASH}</td>
                              <td className="py-2 font-mono">{row.framedIp ?? EM_DASH}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </section>
              ) : report.carrier.verizon.configured ? (
                <p className="text-sm text-muted-foreground">Verizon was skipped because the inferred carrier differs.</p>
              ) : null}

              {report.carrier.vodafone.queried ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">Vodafone</h3>
                  {report.carrier.vodafone.error != null ? (
                    <p className="text-sm text-destructive">{report.carrier.vodafone.error}</p>
                  ) : (
                    <dl className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ICCID</dt>
                        <dd className="font-mono text-sm">{report.carrier.vodafone.iccid ?? EM_DASH}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">State</dt>
                        <dd className="text-sm">{report.carrier.vodafone.state ?? EM_DASH}</dd>
                      </div>
                    </dl>
                  )}
                </section>
              ) : null}

              {report.carrier.att.queried ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">AT&amp;T</h3>
                  {report.carrier.att.error != null ? (
                    <p className="text-sm text-destructive">{report.carrier.att.error}</p>
                  ) : (
                    <dl className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ICCID</dt>
                        <dd className="font-mono text-sm">{report.carrier.att.iccid ?? EM_DASH}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">IMEI</dt>
                        <dd className="font-mono text-sm">{report.carrier.att.imei ?? EM_DASH}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</dt>
                        <dd className="text-sm">{report.carrier.att.status ?? EM_DASH}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rate plan</dt>
                        <dd className="text-sm">{report.carrier.att.ratePlan ?? EM_DASH}</dd>
                      </div>
                    </dl>
                  )}
                </section>
              ) : report.carrier.att.configured && report.identity.iccid == null ? (
                <p className="text-sm text-muted-foreground">
                  AT&amp;T lookup was skipped because no ICCID is on file for this IMEI, and AT&amp;T Control Center does not support single-query IMEI search.
                </p>
              ) : null}
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
